import { convertValueToUrlParam as convertAirportsToUrlParam } from 'component/Search/Airport/__helper/convert';
import { convertValueToUrlParam as convertAllocationToUrlParam } from 'component/Search/Occupancy/__helper/convert';

import DateUtils, { DateFormat } from 'utility/Format/Date/DateUtils';

import { Iata } from 'types/Search/airport';
import { AirportValue } from 'types/Search/filter';
import {
    Destination,
    DurationType,
    DurationValue,
    GeoFence,
    Occupancy,
    Pagination,
    Search,
    SearchStep,
    Sorting,
    TravelPeriod,
} from 'types/Search/form';
import { paramDurationExactValue, SearchParam } from 'types/Search/params';

import { convertFilterToUrlParams } from './filterToUrlParams';
import { pathNameToSearchStepMap } from './urlToSearch';

const convertDestinationToUrlParams = ({
    hotelId,
    cityIdList,
    directSearch,
    countryIdList,
    regionGroupIdList,
    regionIdList,
    ctsDestination,
    flightDistance,
    directHotelSearchId,
    geoFence,
}: Destination): URLSearchParams => {
    const urlParams = new URLSearchParams();

    if (hotelId) {
        urlParams.append(SearchParam.HotelId, hotelId.toString());
    }
    if (cityIdList.size) {
        urlParams.append(SearchParam.CityId, Array.from(cityIdList).join(','));
    }
    if (directSearch) {
        urlParams.append(SearchParam.DirectSearch, directSearch);
    }
    if (countryIdList.size) {
        urlParams.append(
            SearchParam.CountryId,
            Array.from(countryIdList).join(',')
        );
    }
    if (regionGroupIdList.size) {
        urlParams.append(
            SearchParam.AreaGroupId,
            Array.from(regionGroupIdList).join(',')
        );
    }
    if (regionIdList.size) {
        urlParams.append(
            SearchParam.AreaId,
            Array.from(regionIdList).join(',')
        );
    }
    if (ctsDestination) {
        urlParams.append(SearchParam.CtsDestination, ctsDestination.toString());
    }
    if (flightDistance) {
        urlParams.append(SearchParam.FlightDistance, flightDistance);
    }
    if (directHotelSearchId) {
        urlParams.append(
            SearchParam.DirectHotelSearchId,
            directHotelSearchId.toString()
        );
    }
    if (geoFence) {
        urlParams.append(
            SearchParam.GeoFence,
            convertGeoFenceToUrlParams(geoFence)
        );
    }

    return urlParams;
};

const convertGeoFenceToUrlParams = (geoFence: GeoFence) => {
    return `${geoFence.northEast.latitude.toFixed(
        7
    )},${geoFence.northEast.longitude.toFixed(
        7
    )}_${geoFence.southWest.latitude.toFixed(
        7
    )},${geoFence.southWest.longitude.toFixed(7)}_${geoFence.zoom}`;
};

const convertDepartureAirportListToUrlParams = (
    airports: AirportValue<Iata>
) => {
    const urlParams = new URLSearchParams();
    if (airports.size) {
        urlParams.append(
            SearchParam.Airport,
            convertAirportsToUrlParam(airports)
        );
    }

    return urlParams;
};

const convertOccupancyToUrlParams = (occupancy: Occupancy) => {
    const { allocation } = occupancy;
    const urlParams = new URLSearchParams();
    if (allocation.length) {
        urlParams.append(
            SearchParam.RoomAllocation,
            convertAllocationToUrlParam(allocation)
        );
    }

    return urlParams;
};

const serializeDurationValue = (duration: DurationValue) => {
    switch (duration.type) {
        case DurationType.Day:
            return duration.amount.toString();
        case DurationType.Range:
            return `${duration.min}-${duration.max}`;
        case DurationType.Week:
            return `${duration.amount}w`;
        case DurationType.Exact:
            return paramDurationExactValue;
    }
};

const convertTravelPeriodToUrlParams = (travelPeriod: TravelPeriod) => {
    const { departureDate, returnDate, duration } = travelPeriod;
    const urlParams = new URLSearchParams();
    if (departureDate) {
        urlParams.append(
            SearchParam.DepartureDate,
            DateUtils.formatDateObject(departureDate, DateFormat.FormatYMD)
        );
    }
    if (returnDate) {
        urlParams.append(
            SearchParam.ReturnDate,
            DateUtils.formatDateObject(returnDate, DateFormat.FormatYMD)
        );
    }
    if (duration.type !== DurationType.Empty) {
        const daysValue = serializeDurationValue(duration);
        if (daysValue) {
            urlParams.append(SearchParam.Days, daysValue);
        }
    }

    return urlParams;
};

const convertPaginationToUrlParams = (pagination: Pagination) => {
    const urlParams = new URLSearchParams();
    if (pagination.currentPage > 1) {
        urlParams.append(SearchParam.Page, pagination.currentPage.toString());
    }

    return urlParams;
};

const convertSortingToUrlParams = (sorting: Sorting) => {
    const urlParams = new URLSearchParams();
    if (sorting.region) {
        urlParams.append(SearchParam.SortRegion, sorting.region);
    }
    if (sorting.hotel) {
        urlParams.append(SearchParam.SortHotel, sorting.hotel);
    }
    if (sorting.offer) {
        urlParams.append(SearchParam.SortOffer, sorting.offer);
    }

    return urlParams;
};

/**
 * merges the newUrlSearchParams from Search-State with the oldUrlSearchParams from location.href
 * @param newUrlSearchParams
 * @param oldUrlSearchParams
 * @returns
 */
const mergeUrlParams = (
    newUrlSearchParams: URLSearchParams,
    oldUrlSearchParams?: URLSearchParams
) => {
    if (!oldUrlSearchParams) {
        return newUrlSearchParams;
    }

    const oldSearchParamsList = [...oldUrlSearchParams];
    const additionalSearchParams = oldSearchParamsList.filter(
        ([key]) => !newUrlSearchParams.get(key)
    );

    const mergedUrlParams = new URLSearchParams({
        ...Object.fromEntries(newUrlSearchParams),
        ...Object.fromEntries(additionalSearchParams),
    });
    mergedUrlParams.sort();

    return mergedUrlParams;
};

export const searchStepToPathNameMap: Partial<Record<SearchStep, string>> =
    Object.fromEntries(
        Object.entries(pathNameToSearchStepMap).map(([key, value]) => [
            value,
            key,
        ])
    );

export const convertSearchToUrlParams = (
    search: Search,
    currentSearchUrl?: URL
): URLSearchParams => {
    // convert Search into urlParams
    const searchUrlParams = new URLSearchParams({
        // destination
        ...Object.fromEntries(
            convertDestinationToUrlParams(search.destination)
        ),
        // departureAirportList
        ...Object.fromEntries(
            convertDepartureAirportListToUrlParams(search.departureAirportList)
        ),
        // occupancy
        ...Object.fromEntries(convertOccupancyToUrlParams(search.occupancy)),
        // travelPeriod
        ...Object.fromEntries(
            convertTravelPeriodToUrlParams(search.travelPeriod)
        ),
        // pagination
        ...Object.fromEntries(convertPaginationToUrlParams(search.pagination)),
        // sorting
        ...Object.fromEntries(convertSortingToUrlParams(search.sorting)),
    });

    // filter
    const filterUrlParams = convertFilterToUrlParams(search.filter);

    const urlParams = new URLSearchParams({
        ...Object.fromEntries(searchUrlParams),
        ...Object.fromEntries(filterUrlParams),
        ...search.unknownParams,
    });
    urlParams.sort();

    return mergeUrlParams(urlParams, currentSearchUrl?.searchParams);
};
